import { DischargePatient } from '~/types/discharge';

export type DischargeDocType =
  | 'discharge_summary'
  | 'pt_ot_notes'
  | 'wound_notes'
  | 'physician_orders'
  | 'insurance_auth'
  | 'agency_narrative';

export interface DischargeReferralDocument {
  id: string;
  title: string;
  type: DischargeDocType;
  content: string;
  dateCreated: string; // human-formatted
}

export interface DischargeReferralPacket {
  assembledAt: string;
  patient: {
    name: string;
    dob: string;
    address: string;
    phone: string;
  };
  insurance: {
    plan: string;
    memberId: string;
  };
  clinicalSummary: {
    diagnoses: string[];
    recentNotes: string;
    keyConditions: string[];
  };
  orders: string[];
  documents: DischargeReferralDocument[];
}

function todayHuman(): string {
  return new Date().toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function todayShort(): string {
  return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function buildReferralPacket(patient: DischargePatient): DischargeReferralPacket {
  const diagnoses = [
    patient.primaryDiagnosis,
    'Hypertension',
    'Type 2 Diabetes',
  ];

  const orders = [
    'Skilled Nursing — initial assessment within 24 hours of admission',
    'Medication reconciliation and patient education',
    'Vital signs monitoring per program protocol',
    `Follow-up with ${patient.attendingPhysician} within 7 days`,
  ];

  const documents: DischargeReferralDocument[] = [
    {
      id: `${patient.id}-doc-summary`,
      title: 'Discharge Summary',
      type: 'discharge_summary',
      dateCreated: todayShort(),
      content: `DISCHARGE SUMMARY\n\nPatient: ${patient.name}\nDOB: ${patient.dob}\nMRN: ${patient.mrn}\nAdmit: ${patient.admitDate}\nFacility: ${patient.facilityName}\nAttending: ${patient.attendingPhysician}\n\nPrimary Diagnosis: ${patient.primaryDiagnosis}\n\nHospital Course:\nPatient admitted ${patient.currentLOS} day(s) ago for ${patient.primaryDiagnosis.toLowerCase()}. Clinically stable for transition to ${patient.disposition.replace(/_/g, ' ')}.\n\nFunctional Status:\nAmbulatory with assistive device as needed. Independent with most ADLs.\n\nDischarge Plan:\nTransition to home-based services. Caregiver support confirmed (${patient.primaryContact.name}, ${patient.primaryContact.relationship}).`,
    },
    {
      id: `${patient.id}-doc-orders`,
      title: 'Physician Orders',
      type: 'physician_orders',
      dateCreated: todayShort(),
      content: `PHYSICIAN ORDERS\n\nOrdering Provider: ${patient.attendingPhysician}\nDate: ${todayShort()}\n\n${orders.map((o, i) => `${i + 1}. ${o}`).join('\n')}`,
    },
    {
      id: `${patient.id}-doc-insurance`,
      title: 'Insurance Authorization',
      type: 'insurance_auth',
      dateCreated: todayShort(),
      content: `INSURANCE AUTHORIZATION\n\nPlan: ${patient.insurance}\nMember ID: ${patient.memberId}\nAuthorization Status: Approved\nEffective: ${todayShort()}\nDuration: 60-day initial episode\n\nCovered services include skilled nursing, medication management, and program-specific therapies.`,
    },
    {
      id: `${patient.id}-doc-narrative`,
      title: 'Agency Referral Narrative',
      type: 'agency_narrative',
      dateCreated: todayShort(),
      content: `AGENCY NARRATIVE\n\nRe: ${patient.name} (DOB ${patient.dob})\n\nWe are referring ${patient.name}, a ${patient.age}-year-old patient under the care of ${patient.attendingPhysician} at ${patient.facilityName}, for post-acute services aligned with the ${patient.disposition.replace(/_/g, ' ')} program.\n\nPrimary diagnosis: ${patient.primaryDiagnosis}. Patient is clinically stable and caregiver-supported. Coordination with ${patient.primaryContact.name} (${patient.primaryContact.relationship}, ${patient.primaryContact.phone}) is already established.\n\nPlease confirm capacity and propose a start-of-care date at your earliest convenience.`,
    },
  ];

  return {
    assembledAt: todayHuman(),
    patient: {
      name: patient.name,
      dob: patient.dob,
      address: `${patient.facilityName} · Floor ${patient.floor} · Rm ${patient.room}`,
      phone: patient.primaryContact.phone,
    },
    insurance: {
      plan: patient.insurance,
      memberId: patient.memberId,
    },
    clinicalSummary: {
      diagnoses,
      recentNotes: `Admitted ${patient.currentLOS} day(s) ago to ${patient.facilityName} for ${patient.primaryDiagnosis.toLowerCase()}. Stable course; ready for post-acute placement. Caregiver engaged.`,
      keyConditions: diagnoses.slice(0, 3),
    },
    orders,
    documents,
  };
}
